import { useEffect, useState } from "react";
import Layout from "../../components/shared/layout/layout";
import LatestNews from "./component/latest-news";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Play,
  Sparkles,
  Newspaper,
  Radio,
  GraduationCap,
  Lightbulb,
} from "lucide-react";

const Resources = () => {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    document.title =
      "Resources | Zulfah Academy — Ideas, Stories & Opportunities";

    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "Empowering Aspiring Technologists | Zulfah Academy",
      author: {
        "@type": "Organization",
        name: "Zulfah Academy",
      },
      publisher: {
        "@type": "Organization",
        name: "Zulfah Academy",
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": "https://zulfahacademy.com/resources",
      },
      description:
        "Stories, insights, opportunities and technology education resources from Zulfah Academy.",
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schema);

    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <Layout>

      {/* ========================================================= */}
      {/* HERO */}
      {/* ========================================================= */}

    


      {/* ========================================================= */}
      {/* RESOURCE CATEGORIES */}
      {/* ========================================================= */}

      <section className="bg-[#f7f9fa] py-24">

        <div className="max-w-7xl mx-auto px-6">

          <div className="max-w-2xl mb-14">

            <p className="text-[#cf375d] font-bold tracking-[0.2em] uppercase text-xs">
              Explore
            </p>

            <h2 className="text-4xl sm:text-5xl font-black text-[#07151c] mt-4">
              More than content.
              <br />
              <span className="text-[#59b5cd]">
                A window into what's next.
              </span>
            </h2>

          </div>


          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">

            {[
              {
                icon: Newspaper,
                title: "News & Stories",
                text: "What we're building, learning and changing.",
              },
              {
                icon: GraduationCap,
                title: "Learning",
                text: "Practical knowledge for the next generation.",
              },
              {
                icon: Lightbulb,
                title: "Ideas",
                text: "Perspectives on technology and innovation.",
              },
              {
                icon: Radio,
                title: "Media",
                text: "Events, interviews and conversations.",
              },
            ].map((item, index) => {

              const Icon = item.icon;

              return (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="group bg-white border border-gray-100 rounded-3xl p-7 shadow-sm hover:shadow-xl transition-all duration-300"
                >

                  <div className="w-12 h-12 rounded-2xl bg-[#07151c] text-white flex items-center justify-center group-hover:bg-[#59b5cd] transition">
                    <Icon className="w-5 h-5" />
                  </div>

                  <h3 className="text-xl font-bold mt-7">
                    {item.title}
                  </h3>

                  <p className="text-gray-500 mt-3 leading-relaxed">
                    {item.text}
                  </p>

                  <div className="mt-7 flex items-center gap-2 text-sm font-semibold text-[#59b5cd]">
                    Explore
                    <ArrowUpRight className="w-4 h-4" />
                  </div>

                </motion.div>
              );
            })}

          </div>

        </div>

      </section>


      {/* ========================================================= */}
      {/* FEATURED MEDIA */}
      {/* ========================================================= */}

      <section className="bg-white py-24">

        <div className="max-w-7xl mx-auto px-6">

          <div className="grid lg:grid-cols-[.9fr_1.1fr] gap-16 items-center">

            {/* Text */}
            <div>

              <p className="text-[#ecb32c] font-bold uppercase tracking-[0.2em] text-xs">
                In the spotlight
              </p>

              <h2 className="text-4xl sm:text-5xl font-black text-[#07151c] mt-5 leading-tight">
                When the world
                <br />
                <span className="text-[#59b5cd]">
                  pays attention.
                </span>
              </h2>

              <p className="text-gray-500 text-lg leading-relaxed mt-7">
                Our work has been featured across leading media platforms,
                helping amplify conversations around technology,
                education and opportunity.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">

                {["TVC News", "Arise TV", "Technology", "Education"].map(
                  (item) => (
                    <span
                      key={item}
                      className="px-4 py-2 rounded-full bg-gray-50 border border-gray-100 text-sm text-gray-600"
                    >
                      {item}
                    </span>
                  )
                )}

              </div>

            </div>


            {/* Video */}
            <motion.div
              whileHover={{ scale: 1.015 }}
              className="relative rounded-[2rem] overflow-hidden bg-[#07151c] shadow-2xl"
            >

              <AnimatePresence mode="wait">

                {!showVideo ? (

                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowVideo(true)}
                    className="relative w-full aspect-video group"
                  >

                    <img
                      src="https://img.youtube.com/vi/OM72DSHXj5s/maxresdefault.jpg"
                      alt="Zulfah Market Outreach"
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition" />

                    <div className="absolute inset-0 flex items-center justify-center">

                      <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition">

                        <Play
                          className="w-7 h-7 text-[#07151c] ml-1"
                          fill="currentColor"
                        />

                      </div>

                    </div>

                    <div className="absolute bottom-6 left-6">

                      <span className="px-4 py-2 rounded-full bg-[#ecb32c] text-[#07151c] text-xs font-bold uppercase tracking-wider">
                        Featured on TVC News
                      </span>

                    </div>

                  </motion.button>

                ) : (

                  <motion.iframe
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src="https://www.youtube.com/embed/OM72DSHXj5s?autoplay=1"
                    title="Zulfah Market Outreach - TVC News"
                    className="w-full aspect-video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />

                )}

              </AnimatePresence>

            </motion.div>

          </div>

        </div>

      </section>


      {/* ========================================================= */}
      {/* LATEST NEWS */}
      {/* ========================================================= */}

      <section id="latest" className="relative bg-[#f7f9fa] py-24">

        <div className="max-w-7xl mx-auto px-6">

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">

            <div>

              <p className="text-[#cf375d] font-bold uppercase tracking-[0.2em] text-xs">
                Latest
              </p>

              <h2 className="text-4xl sm:text-5xl font-black text-[#07151c] mt-4">
                What we're
                <span className="text-[#59b5cd]">
                  {" "}talking about.
                </span>
              </h2>

            </div>

            <p className="max-w-md text-gray-500">
              Discover the latest stories, announcements,
              opportunities and conversations from Zulfah.
            </p>

          </div>

          <LatestNews />

        </div>

      </section>


      {/* ========================================================= */}
      {/* FINAL CTA */}
      {/* ========================================================= */}

      <section className="relative overflow-hidden bg-[#07151c] text-white py-32">

        <div className="absolute inset-0">

          <div className="absolute w-[30rem] h-[30rem] bg-[#59b5cd]/20 blur-[150px] rounded-full -top-40 -left-40" />

          <div className="absolute w-[30rem] h-[30rem] bg-[#ecb32c]/15 blur-[150px] rounded-full -bottom-40 -right-40" />

        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">

          <Sparkles className="w-7 h-7 text-[#ecb32c] mx-auto mb-7" />

          <p className="text-xs uppercase tracking-[0.3em] text-white/40">
            Stay curious
          </p>

          <h2 className="text-4xl sm:text-6xl font-black mt-5 leading-tight">
            The future doesn't
            <br />
            <span className="text-[#59b5cd]">
              wait for anyone.
            </span>
          </h2>

          <p className="max-w-2xl mx-auto mt-7 text-lg text-white/50 leading-relaxed">
            Keep learning. Keep building. Keep asking better questions.
            The next breakthrough could start with you.
          </p>

          <div className="mt-10">

            <a
              href="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white text-[#07151c] font-bold hover:scale-105 transition"
            >
              Start a conversation

              <ArrowUpRight className="w-5 h-5" />

            </a>

          </div>

        </div>

      </section>

    </Layout>
  );
};

export default Resources;